import { useMemo } from 'react';
import { EbookTemplate } from '../../../mocks/ebookTemplates';

interface EbookOfTheWeekProps {
  allTemplates: EbookTemplate[];
  onPreview: (t: EbookTemplate) => void;
  onUse: (t: EbookTemplate) => void;
  isFavorite: boolean;
  onToggleFavorite: (id: string) => void;
}

// ── Deterministic weekly pick ─────────────────────────────────────────────────
function getWeeklyTemplate(templates: EbookTemplate[]): EbookTemplate {
  const nowMs = Date.now();
  const weekIndex = Math.floor(nowMs / (7 * 24 * 60 * 60 * 1000));
  const pool = templates.filter(t => t.rating >= 4.8 && t.downloads >= 12000);
  const source = pool.length >= 3 ? pool : templates.filter(t => t.rating >= 4.7);
  return source[weekIndex % source.length] ?? templates[0];
}

// ── Days until next Monday ────────────────────────────────────────────────────
function daysUntilMonday(): number {
  const now = new Date();
  const day = now.getDay(); // 0 = Sun, 1 = Mon …
  return day === 1 ? 7 : (8 - day) % 7 || 7;
}

// ── AI spotlight description generator ───────────────────────────────────────
function buildSpotlightDesc(t: EbookTemplate): string {
  const catLines: Record<string, string> = {
    'Business & Entrepreneurship':
      'Whether you\'re validating a startup idea or scaling a seven-figure operation, this template hands you a battle-tested roadmap used by thousands of founders.',
    'Self-Help & Personal Development':
      'Rooted in behavioral psychology and real-world success stories, this template gives readers actionable frameworks they can apply the same day they finish reading.',
    'Technology & Programming':
      'From first principles to production-ready projects, this template strips away the noise and gets readers writing real code faster than any tutorial ever could.',
    'Health & Wellness':
      'Combining evidence-based nutrition science with practical lifestyle engineering, this template is the only wellness guide your audience will actually finish.',
    'Finance & Investing':
      'With clear explanations, real portfolio examples, and zero jargon, this template turns complex financial concepts into confident, actionable decisions.',
    'Creative & Arts':
      'Beautifully structured to mirror the creative process itself, this template turns raw artistic instinct into a publishable, sellable body of work.',
    'Fiction & Storytelling':
      'With a narrative architecture that keeps readers glued from page one, this template is the closest thing to a bestseller blueprint a writer can get.',
    'Marketing & Sales':
      'Packed with conversion-focused copy frameworks and real campaign breakdowns, this template is the unfair advantage every marketer needs on their shelf.',
    'Career & Leadership':
      'Distilling decades of leadership research into clear, weekly habits, this template helps managers inspire teams and executives drive lasting organizational change.',
    'Education & Academic':
      'Grounded in learning science and spaced-repetition principles, this template transforms dense academic material into content that actually sticks.',
  };
  const base =
    catLines[t.category] ??
    `Built for readers who are serious about ${t.category.toLowerCase()}, this template blends expert insight with practical structure to create an eBook that stands out on any shelf.`;

  const tagLine =
    t.tags.includes('Minimal') || t.tags.includes('Clean')
      ? ' Its minimalist layout keeps focus on your words, not the design.'
      : t.tags.includes('Bold') || t.tags.includes('Dark')
      ? ' The bold, high-contrast design commands attention from the first page.'
      : t.tags.includes('Elegant') || t.tags.includes('Warm')
      ? ' Its warm, elegant typography gives every chapter a premium, editorial feel.'
      : ' Every layout decision was made to maximize readability and reader engagement.';

  return base + tagLine;
}

const TAG_STYLE_MAP: Record<string, { icon: string; bg: string; text: string }> = {
  Minimal: { icon: 'ri-subtract-line', bg: 'bg-white/10', text: 'text-white/80' },
  Bold: { icon: 'ri-contrast-2-line', bg: 'bg-white/10', text: 'text-white/80' },
  Dark: { icon: 'ri-moon-line', bg: 'bg-white/10', text: 'text-white/80' },
  Colorful: { icon: 'ri-palette-line', bg: 'bg-white/10', text: 'text-white/80' },
  Elegant: { icon: 'ri-star-line', bg: 'bg-white/10', text: 'text-white/80' },
  Modern: { icon: 'ri-sparkling-line', bg: 'bg-white/10', text: 'text-white/80' },
  Corporate: { icon: 'ri-briefcase-line', bg: 'bg-white/10', text: 'text-white/80' },
  Creative: { icon: 'ri-magic-line', bg: 'bg-white/10', text: 'text-white/80' },
  Clean: { icon: 'ri-leaf-line', bg: 'bg-white/10', text: 'text-white/80' },
  Warm: { icon: 'ri-sun-line', bg: 'bg-white/10', text: 'text-white/80' },
  Academic: { icon: 'ri-graduation-cap-line', bg: 'bg-white/10', text: 'text-white/80' },
  Inspiring: { icon: 'ri-heart-line', bg: 'bg-white/10', text: 'text-white/80' },
  Technical: { icon: 'ri-code-s-slash-line', bg: 'bg-white/10', text: 'text-white/80' },
  Futuristic: { icon: 'ri-rocket-line', bg: 'bg-white/10', text: 'text-white/80' },
};

export default function EbookOfTheWeek({
  allTemplates,
  onPreview,
  onUse,
  isFavorite,
  onToggleFavorite,
}: EbookOfTheWeekProps) {
  const template = useMemo(() => getWeeklyTemplate(allTemplates), [allTemplates]);
  const description = useMemo(() => buildSpotlightDesc(template), [template]);
  const refreshIn = daysUntilMonday();
  const readHours = Math.max(1, Math.round(template.pages / 25));

  // Derive a rich dark bg color from the template accent
  const bgStyle = {
    background: `linear-gradient(135deg, ${template.color}ee 0%, ${template.color}99 40%, #0f172a 100%)`,
  };

  const BADGE_COLORS: Record<string, string> = {
    Popular: 'bg-amber-400/30 text-amber-200 border-amber-400/40',
    New: 'bg-emerald-400/30 text-emerald-200 border-emerald-400/40',
    Trending: 'bg-rose-400/30 text-rose-200 border-rose-400/40',
  };

  return (
    <section className="max-w-7xl mx-auto px-6 pb-4 pt-2">
      <div
        className="relative rounded-3xl overflow-hidden"
        style={bgStyle}
      >
        {/* Decorative bg blobs */}
        <div
          className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{ background: template.color, transform: 'translate(30%, -30%)' }}
        />
        <div
          className="absolute bottom-0 left-1/3 w-72 h-72 rounded-full opacity-10 blur-3xl pointer-events-none"
          style={{ background: template.color, transform: 'translateY(40%)' }}
        />

        <div className="relative flex flex-col lg:flex-row">

          {/* ── Left: Cover Image ──────────────────────────────────────── */}
          <div className="lg:w-[38%] relative flex-shrink-0">
            <div
              className="relative overflow-hidden cursor-pointer group"
              style={{ minHeight: '340px', maxHeight: '460px', height: '100%' }}
              onClick={() => onPreview(template)}
            >
              <img
                src={template.image}
                alt={template.name}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                style={{ minHeight: '340px', maxHeight: '460px' }}
              />
              {/* Gradient blend into right panel */}
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(to right, transparent 0%, transparent 60%, ${template.color}dd 100%), linear-gradient(to bottom, transparent 40%, rgba(15,23,42,0.5) 100%)`,
                }}
              />

              {/* SPOTLIGHT floating label on image */}
              <div className="absolute top-5 left-5 flex flex-col gap-2">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-black/40 backdrop-blur-md rounded-full border border-white/20">
                  <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></div>
                  <span className="text-xs font-extrabold text-white tracking-widest uppercase">Spotlight</span>
                </div>
                {template.badge && (
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border backdrop-blur-md ${BADGE_COLORS[template.badge] ?? ''}`}>
                    <i className={template.badge === 'Popular' ? 'ri-fire-line' : template.badge === 'New' ? 'ri-sparkling-line' : 'ri-trending-up-line'}></i>
                    {template.badge}
                  </span>
                )}
              </div>

              {/* Download count chip bottom-left */}
              <div className="absolute bottom-5 left-5 flex items-center gap-1.5 px-3 py-1.5 bg-black/50 backdrop-blur-md rounded-full border border-white/15">
                <i className="ri-download-line text-white/80 text-sm"></i>
                <span className="text-white text-sm font-bold">{template.downloads.toLocaleString()}</span>
                <span className="text-white/60 text-xs">uses</span>
              </div>

              {/* Preview hover overlay */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
                <div className="bg-white/95 rounded-xl px-4 py-2.5 flex items-center gap-2 shadow-xl">
                  <i className="ri-eye-line text-slate-700 text-base"></i>
                  <span className="text-sm font-bold text-slate-700">Preview Pages</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Right: Content ─────────────────────────────────────────── */}
          <div className="flex-1 px-8 py-8 flex flex-col justify-between">

            {/* Top section */}
            <div>
              {/* Label row */}
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 px-3.5 py-1.5 bg-white/15 backdrop-blur-sm rounded-full border border-white/25">
                    <i className="ri-trophy-line text-amber-300 text-sm"></i>
                    <span className="text-sm font-extrabold text-white">eBook of the Week</span>
                  </div>
                  {/* Limited Spotlight badge */}
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border bg-amber-400/20 border-amber-400/50 backdrop-blur-sm">
                    <i className="ri-time-line text-amber-300 text-xs animate-pulse"></i>
                    <span className="text-xs font-extrabold text-amber-200 uppercase tracking-wider">Limited Spotlight</span>
                  </div>
                </div>
                {/* Refresh countdown */}
                <div className="flex items-center gap-1.5 text-white/50 text-xs font-medium">
                  <i className="ri-refresh-line text-sm"></i>
                  <span>Refreshes in <strong className="text-white/70">{refreshIn} day{refreshIn !== 1 ? 's' : ''}</strong></span>
                </div>
              </div>

              {/* Category pill */}
              <div className="mb-3">
                <span
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold border"
                  style={{ background: 'rgba(255,255,255,0.12)', borderColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.9)' }}
                >
                  <i className="ri-grid-line text-xs"></i>
                  {template.category}
                </span>
              </div>

              {/* Title */}
              <h2 className="text-3xl font-extrabold text-white leading-tight mb-3 tracking-tight">
                {template.name}
              </h2>

              {/* AI Description */}
              <p className="text-white/75 text-sm leading-relaxed mb-5 max-w-xl">
                {description}
              </p>

              {/* Stats row */}
              <div className="flex flex-wrap gap-4 mb-5">
                {/* Rating */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map(n => (
                      <i
                        key={n}
                        className={`text-sm ${n <= Math.round(template.rating) ? 'ri-star-fill text-amber-400' : 'ri-star-line text-white/25'}`}
                      ></i>
                    ))}
                  </div>
                  <span className="text-white font-extrabold text-sm">{template.rating}</span>
                </div>

                <div className="w-px h-5 bg-white/20"></div>

                {/* Chapters */}
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-book-open-line text-white/60 text-sm"></i>
                  </div>
                  <span className="text-white/90 text-sm font-bold">{template.chapters}</span>
                  <span className="text-white/50 text-xs">chapters</span>
                </div>

                <div className="w-px h-5 bg-white/20"></div>

                {/* Pages */}
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-pages-line text-white/60 text-sm"></i>
                  </div>
                  <span className="text-white/90 text-sm font-bold">{template.pages}</span>
                  <span className="text-white/50 text-xs">pages</span>
                </div>

                <div className="w-px h-5 bg-white/20"></div>

                {/* Read time */}
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-time-line text-white/60 text-sm"></i>
                  </div>
                  <span className="text-white/90 text-sm font-bold">~{readHours}h</span>
                  <span className="text-white/50 text-xs">read</span>
                </div>
              </div>

              {/* Style tag chips */}
              <div className="flex items-center gap-2 flex-wrap mb-6">
                <span className="text-white/40 text-xs font-medium">Style:</span>
                {template.tags.map(tag => {
                  const s = TAG_STYLE_MAP[tag] ?? { icon: 'ri-price-tag-3-line', bg: 'bg-white/10', text: 'text-white/80' };
                  return (
                    <div
                      key={tag}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-white/15 ${s.bg}`}
                    >
                      <i className={`${s.icon} text-xs ${s.text}`}></i>
                      <span className={`text-xs font-semibold ${s.text} whitespace-nowrap`}>{tag}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── CTAs + extras ───────────────────────────────────────── */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => onUse(template)}
                className="flex items-center gap-2 px-6 py-3 bg-white text-slate-900 font-extrabold rounded-xl hover:bg-slate-50 transition-all text-sm whitespace-nowrap cursor-pointer shadow-xl shadow-black/30"
              >
                <i className="ri-edit-line text-base"></i>
                Use This Template
              </button>
              <button
                onClick={() => onPreview(template)}
                className="flex items-center gap-2 px-5 py-3 bg-white/15 backdrop-blur-sm border border-white/25 text-white font-bold rounded-xl hover:bg-white/25 transition-all text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-eye-line text-base"></i>
                Preview Pages
              </button>
              <button
                onClick={() => onToggleFavorite(template.id)}
                className={`w-11 h-11 flex items-center justify-center rounded-xl border transition-all cursor-pointer ${
                  isFavorite
                    ? 'bg-rose-500 border-rose-400 text-white shadow-lg shadow-rose-500/40'
                    : 'bg-white/10 border-white/20 text-white/70 hover:bg-rose-500/20 hover:border-rose-400/40 hover:text-rose-300'
                }`}
              >
                <i className={`${isFavorite ? 'ri-heart-fill' : 'ri-heart-line'} text-lg`}></i>
              </button>

              {/* Social proof micro-copy */}
              <div className="ml-auto flex items-center gap-2 text-white/40 text-xs font-medium">
                <i className="ri-group-line text-sm"></i>
                <span>{template.downloads.toLocaleString()} creators chose this</span>
              </div>
            </div>

          </div>
        </div>

        {/* Bottom accent bar */}
        <div className="h-1 w-full" style={{ background: `linear-gradient(to right, ${template.color}, transparent)` }} />
      </div>
    </section>
  );
}
